import React from "react";
import { useNavigate } from "react-router-dom";
import StatusPill from "./StatusPill";
import { getLeagueStatus } from "./leagueStatus";
import {
  assetLabel,
  formatPrize,
  gameLengthLabel,
  metaLine,
  returnDisplay,
} from "./leagueFormat";
import { AvatarPersonIcon, ChevronRightIcon } from "./icons";
import LeagueStatusLine from "./LeagueStatusLine";
import useMinuteTick from "./useMinuteTick";
import { competitionDestination } from "../../../utils/leaguePath";

// Whoever is top of the board, with their picture. The client's files draw a
// 16px circle on the phone and a 20px one on desktop, falling back to a person
// glyph — the same treatment the View All rows use.
const LeaderAvatar = ({ src }) => (
  <span className="flex h-4 w-4 md:h-5 md:w-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F3F4F6]">
    {src ? (
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover"
        onError={(e) => {
          e.currentTarget.style.visibility = "hidden";
        }}
      />
    ) : (
      <AvatarPersonIcon className="h-2.5 w-2.5 md:h-3 md:w-3" />
    )}
  </span>
);

// The second cash game, beside the hero on League Play home.
//
// Client: "I will always have two cash games up. One upcoming and one live...
// i have focused on what the Starting soon hero will look like and what the
// secondary Live Now image will look like." So this card is built to their Live
// Now design, in both of the files they sent:
//
//   status and the countdown on one row, then the title, then the metadata
//   line, then a filled panel carrying LEADING RETURN and the LEADER, then
//   View Leaderboard on the bottom right
//
// The two files differ in where the countdown and the prize live, and that is
// followed rather than averaged:
//
//   MOBILE   "Ends in 2d" sits top right beside the pill, and the prize is the
//            first segment of the metadata line
//   DESKTOP  the metadata line drops the prize, and Prize Pool / Ends In become
//            their own two-column row under the panel
//
// "Game replaces League" (their View All note) is applied to the desktop file's
// "7-Day League" too, so the two breakpoints cannot describe the same game with
// two different nouns.
//
// The previous version of this card carried a "Active competition. Leaderboard
// view only." callout. It is not in either of these files and has been dropped
// — the panel now says the same thing by showing the board instead of
// describing it.
const FeaturedCashCompactCard = ({ game }) => {
  const navigate = useNavigate();
  const status = getLeagueStatus(game);
  const isLive = status?.tone === "live";
  const isEnded = status?.tone === "ended";
  const ctaLabel = isLive ? "View Leaderboard" : isEnded ? "View Results" : "View Game";
  const prize = formatPrize(game?.cash_prize_amount);
  const leader = game?.leading_player;
  const ret = returnDisplay(game?.leading_performance);

  useMinuteTick(!!status?.countdownValue);

  // The countdown had a desktop-only cell of its own beside a desktop-only
  // PRIZE POOL cell. Moving the prize into the panel left that row with one
  // thing in it, so the row is gone and LeagueStatusLine — already the correct
  // tokens, already in the header — now runs at both breakpoints instead of
  // being md:hidden. One countdown, one colour rule, no breakpoint fork.
  const asset = assetLabel(game);
  const length = gameLengthLabel(game);

  // Client, League Play Dev Request, "Leaderboard Routing": "'View Leaderboard'
  // CTA from a live public league must open the live leaderboard directly. -
  // outside the league shell."
  //
  // The CTA on this card already says "View Leaderboard" while the game is live
  // and "View Game" once it is not, so the destination follows the label rather
  // than contradicting it: a live game opens its board on its own page inside
  // the main QUSHi site, and an upcoming or finished one still opens the game.
    /* Client: "All of league play should carry the same state logic, including
       cash games." This tested `isLive` alone, so a FINISHED featured game fell
       through to /contest/<slug> — the landing page — instead of the leaderboard
       preview that Completed is supposed to open. competitionDestination is the
       shared rule; the label follows the same state so the button can never name
       one destination and open another. */
  const go = () => navigate(competitionDestination(game, status));

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          go();
        }
      }}
      className="rounded-3xl bg-white border border-gray-100 shadow-sm cursor-pointer h-full flex flex-col gap-2.5 px-5 py-4 md:rounded-[2rem] md:p-8 md:gap-4 md:shadow-lg"
    >
      {/* Pill left, status line right, at every width.

          Client, after seeing this card beside My Leagues: "Pin the Live pill's
          baseline to the Ends in 9h, so both share the same baseline. Match
          Ends in 9h to the font size and weight used in My Leagues. Change it
          to QUSHi green #35B23C. Shift Ends in 9h slightly left so its right
          edge aligns with the right edge of View Leaderboard, leaving the
          chevron in its own gutter."

          All four fall out of two changes:

            • `items-baseline` instead of `items-center`, so the pill's text and
              the status line sit on one baseline rather than being centred
              against each other's boxes.
            • LeagueStatusLine instead of a span of this card's own. That is the
              component My Leagues renders, so the size (text-sm), the weight
              (font-semibold) and the colour (#35B23C live, #006565 upcoming,
              grey ended) are the same by construction, not by being copied.
              This card had been writing its own 11px bold #004C50 — which is
              none of the four tokens, and is exactly the drift the client is
              asking us to stop.

          `mr-5` is the chevron's gutter: ChevronRightIcon is w-4 (16px) and the
          bottom row's gap-1 is 4px, so 20px puts this line's right edge on
          "View Leaderboard"'s right edge rather than on the chevron's. */}
      <div className="flex items-baseline justify-between gap-3">
        {status && <StatusPill status={status} withIcon={false} variant="cash" />}
        <LeagueStatusLine game={game} className="mr-5" />
      </div>

      <div>
        <h3 className="font-montserrat font-extrabold text-base md:text-xl leading-tight text-[#0F172A] line-clamp-2">
          {game?.title}
        </h3>
        {/* Client: "I am moving the Prize pool out of the metadata and into
            the gray box." So the metadata line is now the asset and the length
            only — the prize has its own cell in the panel below, where it reads
            as a figure rather than as the tail of a sentence. */}
        <p className="mt-0.5 md:mt-1 text-xs font-medium text-secondary">
          {metaLine(asset, length)}
        </p>
      </div>

      {/* The panel renders when it has EITHER figure to show.

          It used to be gated on `ret` alone, which was right while the prize
          still lived in the metadata line and the panel only ever carried the
          board. Once the client moved the prize in here, that gate started
          hiding the prize too: a live game whose leaderboard has not populated
          showed no prize anywhere on the card. Their own note — "I couldn't get
          the Leading Return section to populate yet because I learned our
          Record data isn't updating over the weekend" — means that is the
          NORMAL state right now, not an edge case.

          A return is still never invented: with no qualifying player the panel
          carries the prize alone rather than a 0.0% nobody achieved. */}
      {(ret || prize) && (
        <div className="rounded-xl border border-gray-100 bg-[#F9FAFB] p-3 md:rounded-2xl md:p-4">
          {/* Client: "I am moving the Prize pool out of the metadata and into
              the gray box… Please keep our eyebrow (gray) and our $money (navy)
              colors consistent; my stitch designs really drift."

              Both eyebrows are therefore the one micro-label treatment this card
              already used for LEADING RETURN, and the figure is #0F172A — the
              navy every other money figure in Cash Games prints. Stitch paints
              this one #001F3F, a second navy, which is the drift being called
              out. The return keeps its own token from returnDisplay. */}
          <div className="flex items-start justify-between gap-3">
            {/* The left cell is the card's primary figure. That is the leading
                return whenever there is one; with no board yet the prize takes
                the slot rather than leaving an empty column beside a
                right-aligned figure — the same position the Starting soon
                card's panel gives it, so the two read as one system. */}
            <div className="min-w-0">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-secondary md:font-bold md:text-tertiary">
                {ret ? "Leading return" : "Prize pool"}
              </span>
              <div
                className={`mt-0.5 font-bold text-xl md:text-3xl md:font-black leading-none ${
                  ret ? ret.cls : "text-[#0F172A]"
                }`}
              >
                {ret ? ret.text : prize}
              </div>
            </div>
            {ret && prize && (
              <div className="shrink-0 text-right">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-secondary md:font-bold md:text-tertiary">
                  Prize pool
                </span>
                <div className="mt-0.5 font-bold text-xl md:text-3xl md:font-black leading-none text-[#0F172A]">
                  {prize}
                </div>
              </div>
            )}
          </div>
          {leader?.user_name && (
            /* Client: "The LEADER eyebrow and the username should share the
               same bottom edge. Both should also be vertically centered
               relative to the user profile picture."

               They did not, because the two spans are different type sizes
               (10px and 12px) and `items-center` centres each one's OWN line
               box — two boxes of different heights centred on the same axis
               have different top and bottom edges.

               Giving both spans the SAME line-height (leading-4, 16px) makes
               their boxes identical, so centring them puts their bottom edges
               on one line by construction, and the avatar — centred on the same
               axis — holds them both. Nothing here is a hand-tuned offset, so a
               later change to either type size cannot quietly break it. */
            <div className="mt-2.5 flex items-center gap-2 border-t border-gray-200/70 pt-2.5">
              <span className="shrink-0 text-[10px] leading-4 font-medium uppercase tracking-wider text-tertiary md:font-bold">
                Leader
              </span>
              <LeaderAvatar src={leader.image} />
              <span className="truncate text-xs leading-4 font-semibold text-[#0F172A]">
                {leader.user_name}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="mt-auto flex justify-end pt-1">
        <span className="inline-flex items-center gap-1 text-sm md:text-base font-semibold text-[#004C50]">
          {ctaLabel}
          <ChevronRightIcon className="h-4 w-4 md:h-5 md:w-5" />
        </span>
      </div>
    </div>
  );
};

export default FeaturedCashCompactCard;
